import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Anchor,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Drop,
  Select,
  Text,
} from "grommet";
import {
  buildAgentAlertsOptions,
  DEFAULT_AGENT_ALERTS_OPTION_ID,
  findUniqueAgentAlertOptionIdByExamLabel,
  formatAgentAlertFieldText,
  getVisiblePodAgentAlertFields,
  splitTextWithUrls,
} from "./agentAlertsUtils";
import { craneDisplayValue } from "./craneSessions";

const preWrapTextStyle = {
  whiteSpace: "pre-wrap",
  wordWrap: "break-word",
  overflowWrap: "break-word",
  maxWidth: "100%",
};

const ALERT_URL_COLOR = "#0066CC";
const HOVER_CLOSE_DELAY_MS = 200;
const COPY_SUCCESS_FEEDBACK_MS = 1500;

const alertUrlBaseStyle = {
  color: ALERT_URL_COLOR,
  fontWeight: 700,
  cursor: "pointer",
  textDecoration: "none",
  backgroundColor: "transparent",
};

const alertUrlActiveStyle = {
  ...alertUrlBaseStyle,
  textDecoration: "underline",
  backgroundColor: "rgba(0, 102, 204, 0.08)",
};

const ATTRIBUTE_ROWS = [
  { key: "instructor", label: "Instructor" },
  { key: "courseName", label: "Course" },
  { key: "examName", label: "Exam" },
  { key: "startDate", label: "Start Date" },
  { key: "endDate", label: "End Date" },
];

const optionCardProps = {
  fill: "horizontal",
  width: "100%",
  pad: "small",
  background: "light-2",
  round: "xsmall",
  border: { color: "border", size: "xsmall" },
  style: { boxSizing: "border-box" },
};

function AgentAlertOptionCard({ option }) {
  if (option.isDefault) {
    return (
      <Box {...optionCardProps} gap="xxsmall">
        <Text size="small" weight="bold" color="rgb(185 43 55)">
          Select teacher if applicable
        </Text>
        <Text size="xsmall" color="dark-4">
          Organization agent alerts
        </Text>
      </Box>
    );
  }

  const visibleRows = ATTRIBUTE_ROWS.filter(({ key }) => option[key]);

  return (
    <Box {...optionCardProps} gap="xxsmall">
      {visibleRows.length > 0 ? (
        visibleRows.map(({ key, label }) => (
          <Box key={key} direction="row" gap="xsmall" wrap>
            <Text size="xsmall" weight="bold">
              {label}:
            </Text>
            <Text size="xsmall">{option[key]}</Text>
          </Box>
        ))
      ) : (
        <Text size="small" color="dark-4">
          Untitled exam
        </Text>
      )}
    </Box>
  );
}

function AlertUrlToken({ url }) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [copyStatus, setCopyStatus] = useState(null);
  const targetRef = useRef(null);
  const openRef = useRef(false);
  const copyStatusRef = useRef(null);
  const hoverCloseTimerRef = useRef(null);
  const copyFeedbackTimerRef = useRef(null);

  copyStatusRef.current = copyStatus;

  const clearHoverCloseTimer = () => {
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  };

  const clearCopyFeedbackTimer = () => {
    if (copyFeedbackTimerRef.current) {
      clearTimeout(copyFeedbackTimerRef.current);
      copyFeedbackTimerRef.current = null;
    }
  };

  const clearAllTimers = () => {
    clearHoverCloseTimer();
    clearCopyFeedbackTimer();
  };

  const closePopover = () => {
    clearAllTimers();
    openRef.current = false;
    setOpen(false);
    setCopyStatus(null);
  };

  const openPopover = () => {
    clearHoverCloseTimer();
    if (!openRef.current) {
      setCopyStatus(null);
    }
    openRef.current = true;
    setOpen(true);
  };

  const scheduleHoverClose = () => {
    if (copyStatusRef.current === "success") return;

    clearHoverCloseTimer();
    hoverCloseTimerRef.current = setTimeout(() => {
      closePopover();
    }, HOVER_CLOSE_DELAY_MS);
  };

  useEffect(() => {
    return () => {
      if (hoverCloseTimerRef.current) {
        clearTimeout(hoverCloseTimerRef.current);
        hoverCloseTimerRef.current = null;
      }
      if (copyFeedbackTimerRef.current) {
        clearTimeout(copyFeedbackTimerRef.current);
        copyFeedbackTimerRef.current = null;
      }
    };
  }, []);

  const copyLink = async () => {
    clearHoverCloseTimer();
    clearCopyFeedbackTimer();

    try {
      await navigator.clipboard.writeText(url);
      setCopyStatus("success");
      copyStatusRef.current = "success";
      copyFeedbackTimerRef.current = setTimeout(() => {
        closePopover();
      }, COPY_SUCCESS_FEEDBACK_MS);
    } catch (error) {
      console.error(error);
      setCopyStatus("error");
      copyStatusRef.current = "error";
    }
  };

  const openLink = () => {
    window.open(url, "_blank", "noopener,noreferrer");
    closePopover();
  };

  const handleUrlKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openPopover();
    }
  };

  const isHighlighted = hovered || focused || open;

  return (
    <>
      <Text
        ref={targetRef}
        as="span"
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={open}
        style={isHighlighted ? alertUrlActiveStyle : alertUrlBaseStyle}
        onMouseEnter={() => {
          setHovered(true);
          openPopover();
        }}
        onMouseLeave={() => {
          setHovered(false);
          scheduleHoverClose();
        }}
        onFocus={() => {
          setFocused(true);
          openPopover();
        }}
        onBlur={() => {
          setFocused(false);
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          openPopover();
        }}
        onKeyDown={handleUrlKeyDown}
      >
        {url}
      </Text>

      {open && targetRef.current && (
        <Drop
          target={targetRef.current}
          align={{ top: "bottom", left: "left" }}
          onClickOutside={closePopover}
          onEsc={closePopover}
        >
          <Box
            pad="xsmall"
            gap="xxsmall"
            background="white"
            elevation="small"
            round="xsmall"
            width={{ min: "small" }}
            onMouseEnter={() => {
              clearHoverCloseTimer();
            }}
            onMouseLeave={() => {
              scheduleHoverClose();
            }}
          >
            {copyStatus === "success" ? (
              <Box aria-live="polite">
                <Text size="small" color="status-ok">
                  Copied to the clipboard
                </Text>
              </Box>
            ) : (
              <>
                <Button label="Copy link" plain size="small" onClick={copyLink} />
                <Button label="Open link" plain size="small" onClick={openLink} />
                {copyStatus === "error" && (
                  <Box aria-live="polite">
                    <Text size="small" color="status-critical">
                      Unable to copy
                    </Text>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Drop>
      )}
    </>
  );
}

function FormattedAlertText({ value }) {
  const formatted = formatAgentAlertFieldText(value);
  const segments = splitTextWithUrls(formatted);

  return (
    <Text style={preWrapTextStyle}>
      {segments.map((segment, index) =>
        segment.type === "url" ? (
          <AlertUrlToken key={`url-${index}-${segment.value}`} url={segment.value} />
        ) : (
          <Text as="span" key={`text-${index}`}>
            {segment.value}
          </Text>
        ),
      )}
    </Text>
  );
}

function OrganizationAgentAlertsContent({ organization }) {
  const alerts = organization?.agent_alerts;

  if (!alerts) {
    return <Text color="dark-4">No alerts</Text>;
  }

  return <FormattedAlertText value={alerts} />;
}

function PodAgentAlertContent({ alert }) {
  const visibleFields = getVisiblePodAgentAlertFields(alert);

  if (visibleFields.length === 0) {
    return <Text color="dark-4">No alerts</Text>;
  }

  return (
    <Box gap="medium">
      {visibleFields.map(({ key, label, value }) => (
        <Box key={key} gap="xsmall">
          <Text weight="bold">{label}</Text>
          <FormattedAlertText value={value} />
        </Box>
      ))}
    </Box>
  );
}

function CraneField({ label, value, href, hideWhenEmpty = false }) {
  const display = craneDisplayValue(value);
  if (!display && hideWhenEmpty) return null;

  return (
    <Box gap="xxsmall">
      <Text weight="bold">{label}</Text>
      {href && display ? (
        <Anchor
          href={href}
          label={display}
          target="_blank"
          rel="noopener noreferrer"
        />
      ) : (
        <Text style={preWrapTextStyle}>{display || "—"}</Text>
      )}
    </Box>
  );
}

function CraneLinkedSession({
  session,
  loading,
  error,
  linked,
  onLink,
}) {
  const examUrl = craneDisplayValue(session?.exam_url);
  const examHref = /^https?:\/\//i.test(examUrl) ? examUrl : "";

  return (
    <Box gap="small" border={{ side: "top", color: "light-4" }} pad={{ top: "medium" }}>
      <Text weight="bold">Crane session</Text>
      {loading && <Text color="dark-4">Loading Crane session...</Text>}
      {error && (
        <Text color="status-critical" role="alert">
          {error}
        </Text>
      )}
      {linked && !loading && !error && !session && (
        <Text color="dark-4">Linked Crane session was not found.</Text>
      )}
      {session && (
        <Box gap="small">
          <CraneField label="Name" value={session.user_name} />
          <CraneField label="Email" value={session.user_email} />
          <CraneField label="Test name" value={session.test_name} />
          <CraneField label="Password" value={session.password} hideWhenEmpty />
          <CraneField
            label="Exam URL"
            value={session.exam_url}
            href={examHref}
            hideWhenEmpty
          />
          <CraneField
            label="Proctor instructions"
            value={session.proctor_instructions}
          />
        </Box>
      )}
      <Button
        label={linked ? "Link a different Crane session" : "Link Crane session"}
        primary
        color="black"
        onClick={onLink}
      />
    </Box>
  );
}

function AgentAlertContent({ selectedOption }) {
  if (!selectedOption) {
    return null;
  }

  if (selectedOption.isDefault) {
    return (
      <OrganizationAgentAlertsContent organization={selectedOption.data} />
    );
  }

  return <PodAgentAlertContent alert={selectedOption.data} />;
}

export default function PodAgentAlertsCard({
  organization,
  podAgentAlerts,
  examValue,
  onSelectionChange,
  craneSession = null,
  craneSessionLoading = false,
  craneSessionError = null,
  craneLinked = false,
  onLinkCraneSession,
}) {
  const selectContainerRef = useRef(null);
  const userHasSelectedRef = useRef(false);
  const [dropWidth, setDropWidth] = useState();
  const [searchText, setSearchText] = useState("");

  const options = useMemo(
    () => buildAgentAlertsOptions(organization, podAgentAlerts),
    [organization, podAgentAlerts],
  );

  const filteredOptions = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    if (!normalizedSearch) return options;

    return options.filter((option) => {
      if (option.isDefault) {
        return option.label.toLowerCase().includes(normalizedSearch);
      }

      return String(option.instructor ?? "")
        .toLowerCase()
        .includes(normalizedSearch);
    });
  }, [options, searchText]);

  const [selectedId, setSelectedId] = useState(DEFAULT_AGENT_ALERTS_OPTION_ID);

  const selectedOption =
    options.find((option) => option.id === selectedId) ?? options[0];

  useEffect(() => {
    userHasSelectedRef.current = false;
  }, [examValue]);

  useEffect(() => {
    if (userHasSelectedRef.current) return;

    const matchedOptionId = findUniqueAgentAlertOptionIdByExamLabel(
      options,
      examValue,
    );
    setSelectedId(matchedOptionId ?? DEFAULT_AGENT_ALERTS_OPTION_ID);
  }, [examValue, options]);

  useLayoutEffect(() => {
    const element = selectContainerRef.current;
    if (!element) return;

    const updateWidth = () => {
      setDropWidth(element.getBoundingClientRect().width);
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return (
    <Card fill height={{ min: "large" }}>
      <CardHeader
        pad="medium"
        border={{ side: "bottom", color: "gray", size: "xsmall" }}
      >
        <Text weight="bold" size="large">
          Agent Alerts
        </Text>
      </CardHeader>

      <CardBody pad="medium" gap="medium">
        <Box ref={selectContainerRef} fill="horizontal">
          <Select
            fill="horizontal"
            options={filteredOptions}
            labelKey="label"
            valueKey={{ key: "id", reduce: true }}
            value={selectedOption?.id}
            searchPlaceholder="Search by instructor"
            emptySearchMessage="No alerts found for that instructor"
            onSearch={(text) => setSearchText(text)}
            onClose={() => setSearchText("")}
            onChange={({ option }) => {
              userHasSelectedRef.current = true;
              setSelectedId(option.id);
              onSelectionChange?.(option);
            }}
            dropProps={{
              stretch: "align",
              width: dropWidth ? `${dropWidth}px` : undefined,
              pad: "xsmall",
            }}
          >
            {(option, index, optionList) => (
              <Box
                fill="horizontal"
                width="100%"
                margin={{
                  bottom: index < optionList.length - 1 ? "small" : undefined,
                }}
              >
                <AgentAlertOptionCard option={option} />
              </Box>
            )}
          </Select>
        </Box>

        <AgentAlertContent selectedOption={selectedOption} />
        {onLinkCraneSession && (
          <CraneLinkedSession
            session={craneSession}
            loading={craneSessionLoading}
            error={craneSessionError}
            linked={craneLinked}
            onLink={onLinkCraneSession}
          />
        )}
      </CardBody>
    </Card>
  );
}
